'use client';

import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import Link from 'next/link';
import { getOptimizedImageUrl } from '@/lib/image-utils';

interface NewsArticleClientProps {
  article: any;
  relatedArticles: any[];
}

export default function NewsArticleClient({ article, relatedArticles }: NewsArticleClientProps) {
  const pageRef = useScrollAnimation({ threshold: 0.1 });

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Social share buttons
  const shareOnTwitter = () => {
    const text = `${article.title} - AI News by VibeMake`;
    const url = window.location.href;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const url = window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section with Featured Image */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-white">
          <div 
            className="w-full h-[300px] sm:h-[400px] lg:h-[500px] bg-center bg-cover bg-no-repeat"
            style={{ 
              backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.6) 100%), url('${getOptimizedImageUrl(article.featuredImage?.node?.sourceUrl, 1440, 600)}')` 
            }}
          >
            <div className="flex items-end h-full">
              <div className="w-full max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 pb-8">
                {/* Category Badge */}
                {article.newsCategories?.nodes?.[0] && (
                  <span className="bg-[#e7131a] text-white px-3 py-1 text-[10px] font-ibm-plex-sans-condensed tracking-wider uppercase rounded">
                    {article.newsCategories.nodes[0].name}
                  </span>
                )}
                
                {/* Title */}
                <h1 className="text-[28px] sm:text-[36px] lg:text-[48px] leading-tight text-white mt-4 mb-4" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                  {article.title}
                </h1>
                
                {/* Meta Info */}
                <div className="flex items-center gap-4 text-white/80 text-[14px] font-ibm-plex-sans">
                  <span>{formatDate(article.date)}</span>
                  {article.author?.node?.name && (
                    <>
                      <span>•</span>
                      <span>by {article.author.node.name}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="w-full flex justify-center">
        <div ref={pageRef} className="animate-on-scroll relative w-full max-w-[1440px] bg-white px-6 sm:px-8 lg:px-12 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Excerpt */}
            {article.excerpt && (
              <div 
                className="text-[18px] sm:text-[20px] font-ibm-plex-sans text-black/80 mb-8 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.excerpt }}
              />
            )}
            
            {/* Main Content */}
            <div 
              className="prose prose-lg max-w-none font-ibm-plex-sans"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            
            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase text-black/50 mb-4">
                Share this article
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={shareOnTwitter}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-[#1DA1F2] hover:text-white transition-all duration-300"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                  </svg>
                </button>
                <button
                  onClick={shareOnLinkedIn}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-[#0077B5] hover:text-white transition-all duration-300"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
                    <circle cx="4" cy="4" r="2"/>
                  </svg>
                </button>
                <button
                  onClick={copyToClipboard}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-black hover:text-white transition-all duration-300"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Tags */}
            {article.tags?.nodes && article.tags.nodes.length > 0 && (
              <div className="mt-8">
                <h3 className="font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase text-black/50 mb-4">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.nodes.map((tag: any) => (
                    <span 
                      key={tag.slug}
                      className="bg-gray-100 text-gray-700 px-3 py-1 text-[12px] font-ibm-plex-sans-condensed tracking-wider uppercase rounded-full"
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="w-full flex justify-center">
          <div className="relative w-full max-w-[1440px] bg-white border-t border-black/10 px-6 sm:px-8 lg:px-12 py-12">
            <h2 className="text-[28px] sm:text-[32px] text-black mb-8" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
              Related Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle) => (
                <Link key={relatedArticle.id} href={`/news/${relatedArticle.slug}`} prefetch={true}>
                  <div className="group bg-white border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-soft hover:opacity-90 cursor-pointer">
                    {/* Image */}
                    <div 
                      className="h-48 bg-center bg-cover bg-no-repeat relative"
                      style={{ 
                        backgroundImage: `url('${getOptimizedImageUrl(relatedArticle.featuredImage?.node?.sourceUrl, 800, 600)}')` 
                      }}
                    >
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-all duration-300" />
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-[20px] text-black mb-2 group-hover:text-[#e7131a] transition-colors" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                        {relatedArticle.title}
                      </h3>
                      <div 
                        className="font-ibm-plex-sans text-[14px] text-black/70 mb-4 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: relatedArticle.excerpt }}
                      />
                      <div className="text-[12px] font-ibm-plex-sans text-black/50">
                        {formatDate(relatedArticle.date)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}